import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField } from '@material-ui/core';
import Button from 'Components/Atoms/Button';
import Checkbox from 'Components/Atoms/Checkbox';
import Typography from 'Components/Atoms/Typography';
import Modal from 'Components/Molecules/Modal';
import { Claim, NoShowProtection, Observation } from 'types/noShowProtection';
import { createClaim } from 'App/NoShowProtection/api';
import {
  apiErrorMessage,
  centsToEuroInput,
  claimCap,
  guestsThatCanBeMissing,
  formatCents,
  formatDateTime,
  LIABILITY_KIND_LABEL,
  newRequestId,
  parseEuroToCents,
  personsStillCovered,
} from 'App/NoShowProtection/domain';
import './ClaimDialog.scss';

const NS = 'noShowProtection';

const NO_SHOW_OPTION = 'noShow';
const FOLLOW_UP_PREFIX = 'followUp:';

const BY_LABEL: Record<Observation['by'], string> = {
  guest: 'by the guest',
  staff: 'by staff',
  system: 'by the system',
};

type ClaimDialogProps = {
  restaurantId: string;
  reservationId: string;
  protection: NoShowProtection;
  claims: Claim[];
  currentGuests: number;
  allowNoShow: boolean;
  onClose: () => void;
  onCreated: () => void;
};

/** Mounted per opening, so the requestId stays stable across retries of one dialog. */
const ClaimDialog: React.FC<ClaimDialogProps> = ({
  restaurantId,
  reservationId,
  protection,
  claims,
  currentGuests,
  allowNoShow,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation(NS);
  const [requestId] = useState(newRequestId);
  const { terms } = protection;
  const fieldId = (field: string) => `claim-${requestId}-${field}`;
  const personCount = (n: number) =>
    n === 1 ? t('1 person') : t('{{n}} persons', { n });

  const claimableObservations = protection.observations.filter(
    (o) => o.kind !== 'noShow' && terms.triggers[o.kind]
  );
  const firstOpen = claimableObservations.find((o) => !o.reviewed);

  const canSelectNoShow = allowNoShow && terms.triggers.noShow;
  const [selectedOccasion, setOccasion] = useState<string | null>(null);
  const occasion =
    selectedOccasion ??
    firstOpen?.id ??
    (canSelectNoShow ? NO_SHOW_OPTION : claimableObservations[0]?.id ?? '');
  const followUpLiabilities = protection.liabilities.filter(
    (l) => l.kind === 'noShow'
  );
  const observation = claimableObservations.find((o) => o.id === occasion);
  const followUp = followUpLiabilities.find(
    (l) => `${FOLLOW_UP_PREFIX}${l.id}` === occasion
  );
  const isNoShow = occasion === NO_SHOW_OPTION;

  const maxMissing = Math.min(
    personsStillCovered(terms, protection.liabilities),
    guestsThatCanBeMissing(currentGuests, protection.liabilities)
  );
  const [missingPersons, setMissingPersons] = useState(String(maxMissing));
  const [onGuestBehalf, setOnGuestBehalf] = useState(false);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cap = useMemo(() => {
    if (followUp) {
      return claimCap({
        terms,
        liabilities: protection.liabilities,
        claims,
        kind: followUp.kind,
        liabilityId: followUp.id,
        occurredAt: followUp.occurredAt,
      });
    }
    if (isNoShow) {
      return claimCap({
        terms,
        liabilities: protection.liabilities,
        claims,
        kind: 'noShow',
        occurredAt: terms.startTs,
        persons: Number(missingPersons),
      });
    }
    if (!observation) return null;
    return claimCap({
      terms,
      liabilities: protection.liabilities,
      claims,
      kind: observation.kind,
      observationId: observation.id,
      occurredAt: observation.ts,
      persons: observation.persons,
    });
  }, [isNoShow, observation, followUp, missingPersons, protection, claims]);

  const [amount, setAmount] = useState('');
  const amountCents = parseEuroToCents(amount);
  const effectiveAmountCents = amount
    ? amountCents
    : cap?.remainingCents ?? NaN;

  const noShowTooEarly = (isNoShow || !!followUp) && Date.now() < terms.startTs;
  const needsGuestConfirmation = !!observation && observation.by !== 'guest';

  const blockingReason = (): string | null => {
    if (!occasion) return t('There is no occasion that can be charged.');
    if (noShowTooEarly)
      return t(
        'A no-show can only be claimed once the reservation has started.'
      );
    if (!cap || (isNoShow && Number(missingPersons) > maxMissing))
      return t('The agreement does not cover this many persons.');
    if (cap.remainingCents < 100)
      return t('The agreed maximum has already been claimed.');
    if (!Number.isFinite(effectiveAmountCents) || effectiveAmountCents < 100)
      return t('The amount must be at least 1,00 €.');
    if (effectiveAmountCents > cap.remainingCents)
      return t('At most {{amount}} can still be claimed.', {
        amount: formatCents(cap.remainingCents),
      });
    if (needsGuestConfirmation && !onGuestBehalf)
      return t(
        'Confirm that the guest cancelled. Cancellations by the restaurant are free of charge.'
      );
    if (!message.trim()) return t('Please write a message to the guest.');
    return null;
  };

  const submit = async () => {
    const reason = blockingReason();
    if (reason) {
      setError(reason);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createClaim(restaurantId, reservationId, {
        requestId,
        kind: followUp
          ? followUp.kind
          : isNoShow
          ? 'noShow'
          : observation!.kind,
        persons: isNoShow ? Number(missingPersons) : undefined,
        observationId: observation?.id,
        liabilityId: followUp?.id,
        onGuestBehalf: needsGuestConfirmation ? onGuestBehalf : undefined,
        amountCents: effectiveAmountCents,
        message: message.trim(),
      });
      onCreated();
      onClose();
    } catch (e) {
      setError(apiErrorMessage(e, t('The fee could not be claimed.')));
    } finally {
      setSubmitting(false);
    }
  };

  const occasionOptions = [
    ...claimableObservations.map((o) => ({
      id: o.id,
      label: t(LIABILITY_KIND_LABEL[o.kind]),
      details: `${formatDateTime(o.ts)} · ${personCount(o.persons)} · ${t(
        BY_LABEL[o.by]
      )}${o.reviewed ? '' : ` (${t('new')})`}`,
      needsConfirmation: o.by !== 'guest',
    })),
    ...followUpLiabilities.map((l) => ({
      id: `${FOLLOW_UP_PREFIX}${l.id}`,
      label: `${t('Further claim')}: ${t(LIABILITY_KIND_LABEL.noShow)}`,
      details: personCount(l.persons),
      needsConfirmation: false,
    })),
    ...(canSelectNoShow
      ? [
          {
            id: NO_SHOW_OPTION,
            label: t(LIABILITY_KIND_LABEL.noShow),
            details: t(
              'A no-show can only be claimed once the reservation has started.'
            ),
            needsConfirmation: false,
          },
        ]
      : []),
  ];

  const chooseOccasion = (id: string) => {
    setOccasion(id);
    setError(null);
    setOnGuestBehalf(false);
    setAmount('');
  };

  return (
    <Modal
      open
      onClose={submitting ? undefined : onClose}
      title={t('Claim fee')}
      maxWidth="sm"
      fullWidth
      role="dialog"
      aria-modal="true"
      aria-labelledby={fieldId('title')}
      titleProps={{ id: fieldId('title') }}
      titleTranslation={null}
      className="claim-fee-modal"
      bodyProps={{ className: 'claim-fee-modal__body' }}
      bottomPart={
        <div className="claim-fee-modal__footer">
          <p className="claim-fee-modal__charge-hint">
            {t(
              'The fee is charged immediately to the deposited payment method.'
            )}
          </p>
          <div className="claim-fee-modal__actions">
            <Button
              variant="outlined"
              translation={null}
              disabled={submitting}
              onClick={onClose}
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="primary"
              translation={null}
              loading={submitting}
              disabled={submitting || !occasion}
              onClick={submit}
            >
              {t('Charge now')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="claim-fee-modal__form">
        <fieldset className="claim-fee-modal__occasions">
          <legend>{t('Occasion')}</legend>
          <div className="claim-fee-modal__choices">
            {occasionOptions.map((option) => {
              const selected = option.id === occasion;
              const optionId = fieldId(`occasion-${option.id}`);
              return (
                <div className="claim-fee-modal__choice" key={option.id}>
                  <label
                    className={`claim-fee-modal__choice-label${
                      selected ? ' is-selected' : ''
                    }`}
                  >
                    <input
                      id={optionId}
                      type="radio"
                      name={fieldId('occasion')}
                      value={option.id}
                      checked={selected}
                      disabled={submitting}
                      aria-labelledby={`${optionId}-label`}
                      aria-describedby={`${optionId}-details`}
                      onChange={() => chooseOccasion(option.id)}
                    />
                    <span className="claim-fee-modal__choice-text">
                      <strong id={`${optionId}-label`}>{option.label}</strong>
                      <span id={`${optionId}-details`}>{option.details}</span>
                    </span>
                  </label>
                  {option.needsConfirmation && (
                    <Checkbox
                      checked={selected && onGuestBehalf}
                      disabled={!selected || submitting}
                      onChange={(_, checked) => setOnGuestBehalf(checked)}
                      inputProps={{ 'aria-describedby': `${optionId}-label` }}
                      label={t('The guest cancelled, not the restaurant')}
                      translation={null}
                      color="primary"
                      className="claim-fee-modal__confirmation"
                    />
                  )}
                </div>
              );
            })}
          </div>
          {!occasionOptions.length && (
            <p className="claim-fee-modal__charge-hint">
              {t('There is no occasion that can be charged.')}
            </p>
          )}
        </fieldset>

        <div className="claim-fee-modal__payment">
          {cap && (
            <p className="claim-fee-modal__cap">
              {t('Agreed max. {{agreed}}, claimed so far {{claimed}}', {
                agreed: formatCents(cap.agreedCents),
                claimed: formatCents(cap.claimedCents),
              })}
            </p>
          )}
          <div className="claim-fee-modal__amount">
            <TextField
              id={fieldId('amount')}
              value={amount}
              placeholder={cap ? centsToEuroInput(cap.remainingCents) : ''}
              fullWidth
              size="small"
              variant="outlined"
              disabled={submitting}
              label={t('Amount (€)')}
              inputProps={{ inputMode: 'decimal' }}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setAmount(e.target.value)}
            />
            <TextField
              id={fieldId('missing-persons')}
              type={isNoShow ? 'number' : 'text'}
              value={
                isNoShow
                  ? missingPersons
                  : String(observation?.persons ?? followUp?.persons ?? '–')
              }
              fullWidth
              size="small"
              variant="outlined"
              disabled={submitting}
              label={t('Missing persons')}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: !isNoShow }}
              inputProps={{ min: 1, max: maxMissing }}
              onChange={(e) => {
                if (!isNoShow) return;
                setMissingPersons(e.target.value);
                setAmount('');
              }}
            />
          </div>
        </div>

        <TextField
          id={fieldId('message')}
          fullWidth
          size="small"
          variant="outlined"
          multiline
          rows={3}
          required
          disabled={submitting}
          value={message}
          label={t('Message to the guest')}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => setMessage(e.target.value)}
        />

        {error && (
          <Typography variant="text-3" color="error" translation={null} block>
            <span role="alert">{error}</span>
          </Typography>
        )}
      </div>
    </Modal>
  );
};

export default ClaimDialog;
